import { useCallback, useMemo } from 'react';
import { OptimiserJob, OptimiserStatus } from './optimiser.types';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { useMyLocation } from '../useLocations';
import { useDaysInMonth } from '../settings/useDates';
import { useOptimiserService } from './OptimiserService';

export const useAllOptimiserJobsForLocation = (): OptimiserJob[] => {
  const locationId = useMyLocation().id;
  const allJobs = useOptimiserService(s => s.jobs);

  return useMemo(() => allJobs.filter(j => j.locationIds.includes(locationId)), [allJobs, locationId]);
};

export const useActiveOptimiserJobs = (): OptimiserJob[] => {
  const allJobs = useAllOptimiserJobsForLocation();

  return useMemo(() => {
    return allJobs.filter(
      j =>
        j.status !== OptimiserStatus.FINISHED &&
        j.status !== OptimiserStatus.DELETED &&
        j.status !== OptimiserStatus.ERROR
    );
  }, [allJobs]);
};

export const useFailedOptimiserJobs = (): OptimiserJob[] => {
  const allJobs = useAllOptimiserJobsForLocation();

  return useMemo(() => {
    return allJobs.filter(j => j.status === OptimiserStatus.ERROR);
  }, [allJobs]);
};

export const useCheckDateOptimising = () => {
  const activeJobs = useActiveOptimiserJobs();
  return useCallback(
    (dateToCheck: Date): boolean => {
      const dateAsString = dateToApiFormat(dateToCheck);

      const activeJob = activeJobs.find(j => {
        const startDateString = dateToApiFormat(j.startDate);
        const endDateString = dateToApiFormat(j.endDate);

        return startDateString <= dateAsString && dateAsString <= endDateString;
      });

      return !!activeJob;
    },
    [activeJobs]
  );
};

export const useIsOptimiserRunning = (date: Date) => {
  const activeJobs = useActiveOptimiserJobs();
  const daysInMonth = useDaysInMonth(date);

  return useMemo(() => {
    const monthStartString = dateToApiFormat(daysInMonth[0]);
    const monthEndString = dateToApiFormat(daysInMonth[daysInMonth.length - 1]);

    const jobInMonth = activeJobs.find(job => {
      const jobStartString = dateToApiFormat(job.startDate);
      const jobEndString = dateToApiFormat(job.endDate);

      const jobStartsInMonth = monthStartString <= jobStartString && jobStartString <= monthEndString;
      const jobEndsInMonth = monthStartString <= jobEndString && jobEndString <= monthEndString;

      return jobStartsInMonth || jobEndsInMonth;
    });

    return !!jobInMonth;
  }, [activeJobs, daysInMonth]);
};
